import { gql } from '@apollo/client';

export const NEIGHBORHOOD_BY_SLUG = gql`
  query NeighborhoodBySlug($slug: String!) {
    findSlug(modelName: "neighborhood", slug: $slug) {
      ... on NeighborhoodEntityResponse {
        data {
          id
          attributes {
            name
            slug
            image {
              data {
                attributes {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`;

// lightweight existence check used by the client-side stale-slug guard
export const NEIGHBORHOOD_EXISTS_BY_SLUG = gql`
  query NeighborhoodExistsBySlug($slug: String!) {
    findSlug(modelName: "neighborhood", slug: $slug) {
      ... on NeighborhoodEntityResponse {
        data {
          id
        }
      }
    }
  }
`;

export const NEIGHBORHOOD_AND_BLOGS_BY_SLUG = gql`
  query NeighborhoodAndBlogsBySlug(
    $slug: String!
    $blogLimit: Int
    $neighborsLimit: Int
    $commuteTimesRightLimit: Int = 50
    $blogsDateFrom: DateTime
  ) {
    blogs(
      filters: {
        neighborhoods: {
          or: [{ slug: { ne: $slug } }, { slug: { null: true } }]
        }
        publishedDate: { gte: $blogsDateFrom }
      }
      pagination: { pageSize: $blogLimit }
      sort: ["publishedDate:DESC"]
    ) {
      data {
        attributes {
          slug
          title
          publishedDate
          shortDescription
          mainImage {
            data {
              attributes {
                url
                formats
              }
            }
          }
        }
      }
    }
    findSlug(modelName: "neighborhood", slug: $slug) {
      ... on NeighborhoodEntityResponse {
        data {
          id
          attributes {
            slug
            name
            description
            city {
              data {
                attributes {
                  name
                }
              }
            }
            image {
              data {
                attributes {
                  url
                  formats
                }
              }
            }
            coordinates
            neighborhoodStats {
              minSalePrice
              maxSalePrice
              saleCount
              averageSalePrice
              averageSaleSqftPrice
              averageRentalPrice
              averageRentalSqftPrice
            }
            commuteTimesLeft {
              timeFirst
              destinationFirst
              transportFirst
              timeSecond
              destinationSecond
              transportSecond
            }
            commuteTimesRight(
              pagination: { pageSize: $commuteTimesRightLimit }
            ) {
              time
              destination
            }
            mainFeaturesCommon {
              type
              description
            }
            walkScore
            walkDescription
            bikeScore
            bikeDescription
            transitScore
            transitDescription
            neighborhoodsNeighbors(pagination: { pageSize: $neighborsLimit }) {
              data {
                attributes {
                  neighborhoodStats {
                    minSalePrice
                    maxSalePrice
                    saleCount
                  }
                  name
                  slug
                  image {
                    data {
                      attributes {
                        url
                        formats
                      }
                    }
                  }
                }
              }
            }
            blogs(
              filters: { publishedDate: { gte: $blogsDateFrom } }
              pagination: { pageSize: $blogLimit }
              sort: "publishedDate:DESC"
            ) {
              data {
                attributes {
                  mainImage {
                    data {
                      attributes {
                        url
                        formats
                      }
                    }
                  }
                  blogTags {
                    data {
                      attributes {
                        label
                      }
                    }
                  }
                  shortDescription
                  title
                  slug
                  publishedDate
                  pageContent
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const NEIGHBORHOOD_PREVIEW_AND_BLOGS_BY_SLUG = gql`
  query NeighborhoodAndBlogsBySlug(
    $slug: String!
    $blogLimit: Int
    $neighborsLimit: Int
    $unsavedChangeId: ID!
  ) {
    blogs(
      filters: {
        neighborhoods: {
          or: [{ slug: { ne: $slug } }, { slug: { null: true } }]
        }
      }
      pagination: { pageSize: $blogLimit }
      sort: ["publishedDate:DESC"]
    ) {
      data {
        attributes {
          slug
          title
          publishedDate
          shortDescription
          mainImage {
            data {
              attributes {
                url
                formats
              }
            }
          }
        }
      }
    }
    findSlug(
      modelName: "neighborhood"
      slug: $slug
      publicationState: "preview"
    ) {
      ... on NeighborhoodEntityResponse {
        data {
          id
          attributes {
            slug
            name
            description
            city {
              data {
                attributes {
                  name
                }
              }
            }
            image {
              data {
                attributes {
                  url
                  formats
                }
              }
            }
            coordinates
            neighborhoodStats {
              minSalePrice
              maxSalePrice
              saleCount
              averageSalePrice
              averageSaleSqftPrice
              averageRentalPrice
              averageRentalSqftPrice
            }
            commuteTimesLeft {
              timeFirst
              destinationFirst
              transportFirst
              timeSecond
              destinationSecond
              transportSecond
            }
            commuteTimesRight {
              time
              destination
            }
            mainFeaturesCommon {
              type
              description
            }
            walkScore
            walkDescription
            bikeScore
            bikeDescription
            transitScore
            transitDescription
            neighborhoodsNeighbors(pagination: { pageSize: $neighborsLimit }) {
              data {
                attributes {
                  neighborhoodStats {
                    minSalePrice
                    maxSalePrice
                    saleCount
                  }
                  name
                  slug
                  image {
                    data {
                      attributes {
                        url
                        formats
                      }
                    }
                  }
                }
              }
            }
            blogs(
              pagination: { pageSize: $blogLimit }
              sort: "publishedDate:DESC"
            ) {
              data {
                attributes {
                  mainImage {
                    data {
                      attributes {
                        url
                        formats
                      }
                    }
                  }
                  blogTags {
                    data {
                      attributes {
                        label
                      }
                    }
                  }
                  shortDescription
                  title
                  slug
                  publishedDate
                  pageContent
                }
              }
            }
          }
        }
      }
    }
    previewButtonUnsavedChange(id: $unsavedChangeId) {
      data {
        attributes {
          data
        }
      }
    }
  }
`;

export const NEIGHBORHOOD_CONDOS = gql`
  query NeighborhoodCondos(
    $nhSlug: String!
    $page: Int
    $pageSize: Int
    $sortBy: String
  ) {
    getNeighborhoodBuildings(
      nhSlug: $nhSlug
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
    ) {
      data {
        id
        slug
        name
        isPreconstruction
        primaryAddress
        yearBuilt
        mainImageUrl
        mainImageFormats
        minSalePrice
        maxSalePrice
        minSaleBeds
        maxSaleBeds
      }
      meta {
        total
        page
        pageSize
        pageCount
      }
    }
  }
`;

export const FAVORITES_BUILDINGS_IN_NEIGHBORHOOD = gql`
  query FavoriteBuildingsInNeighborhood($nhSlug: String, $userId: ID) {
    favoriteBuildings(
      filters: {
        author: { id: { eq: $userId } }
        building: { neighborhood: { slug: { eq: $nhSlug } } }
      }
    ) {
      data {
        id
        attributes {
          building {
            data {
              id
            }
          }
        }
      }
    }
  }
`;

export const BUILDINGS_FOR_MAP = gql`
  query BuildingsForMap($id: ID) {
    buildings(
      filters: { neighborhood: { id: { eq: $id } } }
      pagination: { pageSize: 1000 }
    ) {
      data {
        id
        attributes {
          name
          primaryAddress
          lat
          lon
        }
      }
    }
  }
`;

export const NEIGHBORHOODS_COORDINATES = gql`
  query NhCoordinates {
    neighborhoods(pagination: { pageSize: 1000 }) {
      data {
        id
        attributes {
          name
          coordinates
        }
      }
    }
  }
`;

export const NEIGHBORHOOD_ITEMS_LIST = gql`
  query NhItemsList($pageSize: Int, $page: Int) {
    neighborhoods(pagination: { pageSize: $pageSize, page: $page }) {
      data {
        attributes {
          slug
        }
      }
    }
  }
`;
